import React, { useState, useContext } from "react";
import { UserContext } from "../context/UserContext";
import {
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Input,
  MenuItem,
} from "@mui/material";
import {
  Event as EventIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarTodayIcon,
  Image as ImageIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Percent as PercentIcon,
  EventSeat as EventSeatIcon,
  Key as KeyIcon,
  Password as PasswordIcon,
  HomeWork as HomeWorkIcon,
} from "@mui/icons-material";
import axios from "axios";
import API_BASE_URL from "../config";

const PostEvent = () => {
  const { user } = useContext(UserContext);
  const [event, setEvent] = useState({
    name: "",
    organizer: "",
    location: "",
    event_date: "",
    event_type: "",
    event_fees: "",
    description: "",
    total_no_of_seats: "",
    image_url: "",
    featured: false,
    razorpay_key_id: "",
    razorpay_key_secret: "",
    commission_percentage: "",
    commission_amount: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const eventTypes = ["Online", "In-Person", "Hybrid"];

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      const file = files[0];
      if (
        file &&
        ["image/jpeg", "image/png", "image/gif", "image/jpg"].includes(
          file.type
        )
      ) {
        setImageFile(file);
        setPreviewImage(URL.createObjectURL(file));
      } else {
        setErrorMessage(
          "❌ Invalid file format. Only JPG, PNG, GIF are allowed."
        );
      }
    } else {
      let updatedEvent = {
        ...event,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "total_no_of_seats" || name === "event_fees") {
        const seats = parseInt(
          name === "total_no_of_seats" ? value : updatedEvent.total_no_of_seats
        );
        const fee = parseFloat(
          name === "event_fees" ? value : updatedEvent.event_fees
        );

        let commission_percentage = 0;
        if (seats < 100) commission_percentage = 1;
        else if (seats < 300) commission_percentage = 3;
        else if (seats < 500) commission_percentage = 5;
        else if (seats <= 1000) commission_percentage = 7;
        else commission_percentage = 8;

        const commission_amount = fee
          ? ((commission_percentage / 100) * fee).toFixed(2)
          : 0;

        updatedEvent.commission_percentage = commission_percentage;
        updatedEvent.commission_amount = commission_amount;
      }

      setEvent(updatedEvent);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return "";

    const formData = new FormData();
    formData.append("image", imageFile);

    try {
      const response = await axios.post(`${API_BASE_URL}/events`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data.image_url;
    } catch (error) {
      console.error("🔥 Image upload failed:", error);
      setErrorMessage("❌ Failed to upload image.");
      return "";
    }
  };

  const handleCommissionPayment = async (commissionAmount) => {
    return new Promise((resolve, reject) => {
      const options = {
        key: "rzp_test_2eOTYJ5qhLc4UV", // Razorpay Key ID
        amount: commissionAmount * 100, // Convert to paise
        currency: "INR",
        name: "Event Commission",
        description: "Commission Payment",
        handler: async function (response) {
          try {
            const commissionData = {
              user_id: user.id,
              event_name: event.name,
              payment_id: response.razorpay_payment_id,
              amount: commissionAmount,
              currency: "INR",
              status: "paid",
            };
            await axios.post(
              `${API_BASE_URL}/events-commissions`,
              commissionData
            );
            resolve();
          } catch (err) {
            reject("❌ Failed to record commission payment.");
          }
        },
        prefill: {
          name: user.name || "",
          email: user.email || "",
        },
        theme: {
          color: "#1976d2",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => reject("❌ Payment Failed"));
      rzp.open();
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setSuccessMessage("");
    setErrorMessage("");

    if (!user) {
      setErrorMessage("❌ You must be logged in to post an event.");
      setSubmitted(false);
      return;
    }

    try {
      const imageUrl = await uploadImage();
      const eventData = {
        ...event,
        user_id: user.id,
        image_url: imageUrl || "",
      };

      // Step 1: Pay commission first before posting event
      await handleCommissionPayment(parseFloat(eventData.commission_amount));

      // Step 2: Post the event after payment is successful
      const response = await axios.post(`${API_BASE_URL}/events`, eventData);

      setSuccessMessage(
        `✅ Event Posted: ${response.data.name} by ${response.data.organizer}`
      );
      setEvent({
        name: "",
        organizer: "",
        location: "",
        event_date: "",
        event_type: "",
        event_fees: "",
        description: "",
        total_no_of_seats: "",
        image_url: "",
        featured: false,
        razorpay_key_id: "",
        razorpay_key_secret: "",
        commission_percentage: "",
        commission_amount: "",
      });
      setPreviewImage(null);
      setImageFile(null);
    } catch (error) {
      console.error(
        "🔥 Error posting event:",
        error.response ? error.response.data : error.message
      );
      setErrorMessage(
        error.response?.data?.error ||
          "❌ Failed to post event. Please check required fields."
      );
    }
    setSubmitted(false);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 5, mb: 4 }}>
      <Card sx={{ p: 4, boxShadow: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="h5"
            color="#42326e"
            fontWeight="bold"
            textAlign="center"
            mb={3}
          >
            🎉 Post an Event
          </Typography>

          {successMessage && (
            <Snackbar
              open
              autoHideDuration={4000}
              onClose={() => setSuccessMessage("")}
            >
              <Alert severity="success">{successMessage}</Alert>
            </Snackbar>
          )}
          {errorMessage && (
            <Snackbar
              open
              autoHideDuration={4000}
              onClose={() => setErrorMessage("")}
            >
              <Alert severity="error">{errorMessage}</Alert>
            </Snackbar>
          )}

          <form onSubmit={handleSubmit}>
            {/* Input fields for event details */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <EventIcon sx={{ color: "#42326e" }} />
              <TextField
                fullWidth
                label="Event Name"
                name="name"
                onChange={handleChange}
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <PersonIcon sx={{ color: "#42326e" }} />
              <TextField
                fullWidth
                label="Organizer Name"
                name="organizer"
                onChange={handleChange}
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <LocationOnIcon sx={{ color: "#42326e" }} />
              <TextField
                fullWidth
                label="Location"
                name="location"
                onChange={handleChange}
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CalendarTodayIcon sx={{ color: "#42326e" }} />
              <TextField
                fullWidth
                label="Event Date"
                name="event_date"
                type="date"
                InputLabelProps={{ shrink: true }}
                onChange={handleChange}
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CurrencyRupeeIcon sx={{ color: "#42326e" }} />
              <TextField
                fullWidth
                label="Event Fee (₹)"
                name="event_fees"
                type="number"
                onChange={handleChange}
                required
                value={event.event_fees}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <DescriptionIcon sx={{ color: "#42326e" }} />
              <TextField
                fullWidth
                label="Description"
                name="description"
                type="text"
                multiline
                rows={3}
                onChange={handleChange}
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <EventSeatIcon sx={{ color: "#42326e" }} />
              <TextField
                fullWidth
                label="Number of Seats"
                name="total_no_of_seats"
                type="number"
                onChange={handleChange}
                required
                value={event.total_no_of_seats}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <KeyIcon sx={{ color: "#42326e" }} />
              <TextField
                fullWidth
                label="Razor Pay Key ID"
                name="razorpay_key_id"
                onChange={handleChange}
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <PasswordIcon sx={{ color: "#42326e" }} />
              <TextField
                fullWidth
                label="Razor Pay Key Secret"
                name="razorpay_key_secret"
                onChange={handleChange}
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <PercentIcon sx={{ color: "#42326e" }} />
              <TextField
                fullWidth
                label="Commission Percentage"
                name="commission_percentage"
                value={event.commission_percentage}
                InputProps={{ readOnly: true }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CurrencyRupeeIcon sx={{ color: "#42326e" }} />
              <TextField
                fullWidth
                label="Commission Amount"
                name="commission_amount"
                value={event.commission_amount}
                InputProps={{ readOnly: true }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <HomeWorkIcon sx={{ color: "#42326e" }} />
              <TextField
                select
                fullWidth
                label="Event Type"
                name="event_type"
                onChange={handleChange}
                required
                value={event.event_type}
              >
                {eventTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <ImageIcon sx={{ color: "#42326e" }} />
              <Input
                type="file"
                name="image"
                onChange={handleChange}
                fullWidth
              />
            </Box>

            {previewImage && (
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <img
                  src={previewImage}
                  alt="Event Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "200px",
                    borderRadius: "10px",
                  }}
                />
              </Box>
            )}

            <Button
              variant="contained"
              fullWidth
              type="submit"
              disabled={submitted}
              sx={{
                borderRadius: 2,
                mt: 2,
                backgroundColor: "#42326e",
                color: "white",
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
            >
              {submitted ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Proceed to Pay Commission"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PostEvent;
